import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import LanguageDetector from 'i18next-browser-languagedetector';

// English namespaces
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enProposals from './locales/en/proposals.json';
import enPackages from './locales/en/packages.json';
import enBilling from './locales/en/billing.json';
import enPublic from './locales/en/public.json';
import enSettings from './locales/en/settings.json';
import enLanding from './locales/en/landing.json';
import enContracts from './locales/en/contracts.json';
import enIntegrations from './locales/en/integrations.json';
import enPreferences from './locales/en/preferences.json';
import enMetrics from './locales/en/metrics.json';
import enEarnings from './locales/en/earnings.json';
import enAddons from './locales/en/addons.json';
import enOnboarding from './locales/en/onboarding.json';
import enTeam from './locales/en/team.json';
import enFollowUps from './locales/en/followUps.json';
import enPublicPage from './locales/en/publicPage.json';
import enBookingRequests from './locales/en/bookingRequests.json';
import enWorkspace from './locales/en/workspace.json';

// Portuguese (Brazil) namespaces
import ptBRCommon from './locales/pt-BR/common.json';
import ptBRAuth from './locales/pt-BR/auth.json';
import ptBRDashboard from './locales/pt-BR/dashboard.json';
import ptBRProposals from './locales/pt-BR/proposals.json';
import ptBRPackages from './locales/pt-BR/packages.json';
import ptBRBilling from './locales/pt-BR/billing.json';
import ptBRPublic from './locales/pt-BR/public.json';
import ptBRSettings from './locales/pt-BR/settings.json';
import ptBRLanding from './locales/pt-BR/landing.json';
import ptBRContracts from './locales/pt-BR/contracts.json';
import ptBRIntegrations from './locales/pt-BR/integrations.json';
import ptBRPreferences from './locales/pt-BR/preferences.json';
import ptBRMetrics from './locales/pt-BR/metrics.json';
import ptBREarnings from './locales/pt-BR/earnings.json';
import ptBRAddons from './locales/pt-BR/addons.json';
import ptBROnboarding from './locales/pt-BR/onboarding.json';
import ptBRTeam from './locales/pt-BR/team.json';
import ptBRFollowUps from './locales/pt-BR/followUps.json';
import ptBRPublicPage from './locales/pt-BR/publicPage.json';
import ptBRBookingRequests from './locales/pt-BR/bookingRequests.json';
import ptBRWorkspace from './locales/pt-BR/workspace.json';

// Spanish namespaces
import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esDashboard from './locales/es/dashboard.json';
import esProposals from './locales/es/proposals.json';
import esPackages from './locales/es/packages.json';
import esBilling from './locales/es/billing.json';
import esPublic from './locales/es/public.json';
import esSettings from './locales/es/settings.json';
import esLanding from './locales/es/landing.json';
import esContracts from './locales/es/contracts.json';
import esIntegrations from './locales/es/integrations.json';
import esPreferences from './locales/es/preferences.json';
import esMetrics from './locales/es/metrics.json';
import esEarnings from './locales/es/earnings.json';
import esAddons from './locales/es/addons.json';
import esOnboarding from './locales/es/onboarding.json';
import esTeam from './locales/es/team.json';
import esFollowUps from './locales/es/followUps.json';
import esPublicPage from './locales/es/publicPage.json';
import esBookingRequests from './locales/es/bookingRequests.json';
import esWorkspace from './locales/es/workspace.json';

const namespaces = ['common', 'auth', 'dashboard', 'proposals', 'packages', 'billing', 'public', 'settings', 'landing', 'contracts', 'integrations', 'preferences', 'metrics', 'earnings', 'addons', 'onboarding', 'team', 'followUps', 'publicPage', 'bookingRequests', 'workspace'] as const;
export type Namespace = (typeof namespaces)[number];

const supportedLanguages = [
  'en',
  'es',
  'pt-BR',
  'fr-FR',
  'it-IT',
  'de-DE',
  'en-GB',
  'pt-PT',
  'es-MX',
  'es-ES',
  'fr-CA',
  'nl-NL',
  'pl-PL',
  'tr-TR',
  'id-ID',
  'ar',
  'he-IL'
];

const rtlLanguages = ['ar', 'he-IL'];

i18n
  .use(ICU)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        dashboard: enDashboard,
        proposals: enProposals,
        packages: enPackages,
        billing: enBilling,
        public: enPublic,
        settings: enSettings,
        landing: enLanding,
        contracts: enContracts,
        integrations: enIntegrations,
        preferences: enPreferences,
        metrics: enMetrics,
        earnings: enEarnings,
        addons: enAddons,
        onboarding: enOnboarding,
        team: enTeam,
        followUps: enFollowUps,
        publicPage: enPublicPage,
        bookingRequests: enBookingRequests,
        workspace: enWorkspace,
      },
      'pt-BR': {
        common: ptBRCommon,
        auth: ptBRAuth,
        dashboard: ptBRDashboard,
        proposals: ptBRProposals,
        packages: ptBRPackages,
        billing: ptBRBilling,
        public: ptBRPublic,
        settings: ptBRSettings,
        landing: ptBRLanding,
        contracts: ptBRContracts,
        integrations: ptBRIntegrations,
        preferences: ptBRPreferences,
        metrics: ptBRMetrics,
        earnings: ptBREarnings,
        addons: ptBRAddons,
        onboarding: ptBROnboarding,
        team: ptBRTeam,
        followUps: ptBRFollowUps,
        publicPage: ptBRPublicPage,
        bookingRequests: ptBRBookingRequests,
        workspace: ptBRWorkspace,
      },
      es: {
        common: esCommon,
        auth: esAuth,
        dashboard: esDashboard,
        proposals: esProposals,
        packages: esPackages,
        billing: esBilling,
        public: esPublic,
        settings: esSettings,
        landing: esLanding,
        contracts: esContracts,
        integrations: esIntegrations,
        preferences: esPreferences,
        metrics: esMetrics,
        earnings: esEarnings,
        addons: esAddons,
        onboarding: esOnboarding,
        team: esTeam,
        followUps: esFollowUps,
        publicPage: esPublicPage,
        bookingRequests: esBookingRequests,
        workspace: esWorkspace,
      },
    },
    ns: namespaces,
    defaultNS: 'common',
    fallbackLng: (code) => {
      if (!code) return ['en'];
      
      if (code.startsWith('pt')) {
        return code === 'pt-BR' ? ['pt-BR', 'en'] : ['pt-BR', 'en'];
      }
      
      if (code.startsWith('es')) {
        return ['es', 'en'];
      }
      
      if (code.startsWith('fr')) {
        return code === 'fr-CA' ? ['fr-FR', 'en'] : ['en'];
      }
      
      return ['en'];
    },
    supportedLngs: supportedLanguages,
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
      excludeCacheFor: ['cimode'],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  
  if (rtlLanguages.includes(lng)) {
    document.documentElement.dir = 'rtl';
  } else {
    document.documentElement.dir = 'ltr';
  }
});

const currentLang = i18n.language;
if (currentLang && rtlLanguages.includes(currentLang)) {
  document.documentElement.dir = 'rtl';
} else {
  document.documentElement.dir = 'ltr';
}

export default i18n;
export { rtlLanguages, supportedLanguages };
